import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, categories } from '@/mocks/siteData';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="mx-auto px-4 md:px-6 max-w-[1200px] py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '91.com',
    },
    publisher: {
      '@type': 'Organization',
      name: '91.com',
    },
    url: `https://91.com/article/${article.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91.com',
      url: 'https://91.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Article Header */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="mx-auto px-4 md:px-6 max-w-[900px]">
              <div className="mb-4">
                <Breadcrumb items={[
                  { label: '首页', href: '/' },
                  ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                  { label: article.title },
                ]} />
              </div>
              <div className="mt-6">
                {category && (
                  <Link to={`/category/${category.slug}`} className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4 whitespace-nowrap cursor-pointer hover:bg-accent-200 transition-colors">
                    {category.name}
                  </Link>
                )}
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3 text-sm text-foreground-500">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {article.readTime} 阅读
                  </span>
                  <span>·</span>
                  <span>91.com</span>
                </div>
              </div>
            </div>
          </section>

          {/* Article Content */}
          <section className="py-10 md:py-14">
            <div className="mx-auto px-4 md:px-6 max-w-[900px]">
              <article className="prose prose-sm md:prose-base max-w-none">
                {article.content.split('\n\n').map((block, index) => {
                  if (block.startsWith('## ')) {
                    return (
                      <h2 key={index} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                        {block.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (block.startsWith('1. ') || block.startsWith('2. ') || block.startsWith('3. ') || block.startsWith('4. ') || block.startsWith('5. ') || block.startsWith('6. ')) {
                    return (
                      <li key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed ml-5 mb-1 list-decimal">
                        {block.replace(/^\d+\.\s/, '')}
                      </li>
                    );
                  }
                  if (block.startsWith('- ')) {
                    return (
                      <li key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed ml-5 mb-1 list-disc">
                        {block.replace('- ', '')}
                      </li>
                    );
                  }
                  if (block.startsWith('**Q:') || block.startsWith('**Q：')) {
                    return (
                      <p key={index} className="text-sm md:text-base font-semibold text-foreground-900 mt-6 mb-1">
                        {block.replace(/\*\*/g, '')}
                      </p>
                    );
                  }
                  if (block.startsWith('A:') || block.startsWith('A：')) {
                    return (
                      <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                        {block}
                      </p>
                    );
                  }
                  return (
                    <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {block}
                    </p>
                  );
                })}
              </article>

              {/* Share */}
              <div className="flex items-center gap-3 mt-12 pt-8 border-t border-background-200/70">
                <span className="text-sm text-foreground-500">分享到：</span>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </a>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="mx-auto px-4 md:px-6 max-w-[900px]">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link key={ra.id} to={`/article/${ra.slug}`} className="group block p-4 rounded-lg bg-background-50 hover:bg-background-50 transition-colors cursor-pointer">
                      <h3 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{ra.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}